import React, { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { useForm, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import {
  LocationContextProps,
  useLocationsContext,
} from "../../../context/LocationsContext";
import CountrySelect from "../../sharedComponents/inputs/CountrySelect";
import StateProvinceSelect from "../../sharedComponents/inputs/StateProvinceSelect";
import {
  FormLocation,
  Location,
  getEstablishmentTypes,
  LocationFormInput,
  LocationAttr,
} from "../Locations/Location";
import {
  UnsavedChangesPrompt,
  Select,
  Input,
  useTreeData,
  Checkbox,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { LocationActions } from "../../../hooks/useLocationMutation";
import { mapDefaultValues } from "./Utils";
import { useQueryClient } from "@tanstack/react-query";

export enum Mode {
  Add,
  Edit,
  View,
}

interface AddEditLocationProps {
  mode: Mode;
  location?: Location;
  setIsSubmitting?: (isSubmitting: boolean) => void;
  setShowAddSlider?: Dispatch<SetStateAction<boolean>>;
  mutations: (
    action: LocationActions,
    input: LocationFormInput,
    locationId?: string
  ) => Promise<any>;
  isLoading: boolean;
  readOnly: boolean;
}

const AddEditLocationFormNew: React.FC<AddEditLocationProps> = ({
  location,
  mode,
  mutations,
  isLoading,
  readOnly,
}) => {
  const locationsContext: LocationContextProps = useLocationsContext();
  const { t } = useTranslation();
  const history = useHistory();

  const treeData = useTreeData();

  const queryClient = useQueryClient();

  const {
    reset,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isDirty },
  } = useForm<LocationFormInput>({
    mode: "onBlur",
    defaultValues: {
      name: "",
      shortCode: "",
      industryDescription: "",
      address: null,
      lineage: "",
      locationStatus: null,
      id: "",
      isPhysicalLocation: false,
      establishmentType: null,
      sicCode: "",
      naicsCode: "",
      ein: "",
    },
  });

  // useWatch will make this value update when the value changes
  const isPhysicalLocation = watch("isPhysicalLocation");

  const countrySelected = watch("address.nation");

  const saveUpdateLocation = async (formLocation: LocationFormInput) => {
    await mutations(LocationActions.UPDATE, formLocation, location.id);

    queryClient.invalidateQueries({ queryKey: ["location"] });

    treeData.refetch();

    return reset();
  };

  const addLocation = async (formLocation: LocationFormInput) => {
    reset();

    const addLocationMutation = await mutations(
      LocationActions.CREATE,
      formLocation,
      location.id
    );

    queryClient.invalidateQueries({ queryKey: ["location"] });

    treeData.refetch();

    return history.push(`/location/${addLocationMutation.id}/edit`);
  };

  const onSubmit = useCallback(
    (formValues: LocationFormInput) => {
      if (mode === Mode.Add) {
        return addLocation(formValues);
      }

      if (mode === Mode.Edit) {
        return saveUpdateLocation(formValues);
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [mode]
  );

  useEffect(() => {
    let defaultValues;
    if (!isLoading && locationsContext.isCountryListLoaded) {
      defaultValues =
        mode !== Mode.Add
          ? mapDefaultValues(
              location as LocationAttr,
              locationsContext.countryList
            )
          : mapDefaultValues({} as LocationAttr, locationsContext.countryList);

      return reset(defaultValues);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isLoading, locationsContext.isCountryListLoaded, mode]);

  return (
    <>
      <form
        id="locationsNewForm"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        <Input
          control={control}
          name="name"
          label={t("universal_Name")}
          rules={{
            required: t("customer_Form_Name_Required"),
            pattern: /^\s*[^>]+\s*$/i,
            maxLength: {
              value: 50,
              message: t("location_Form_Name_Max_Length_Validation"),
            },
          }}
          disabled={mode === Mode.View}
          errorMessage={errors ? errors.name?.message : undefined}
          readOnly={readOnly}
        />
        <Input
          control={control}
          name="shortCode"
          label={t("location_form_short_code_label")}
          disabled={mode === Mode.View}
          errorMessage={errors ? errors.shortCode?.message : undefined}
          readOnly={readOnly}
        />
        <Input
          control={control}
          name="industryDescription"
          label={t("location_form_industry_description_label")}
          disabled={mode === Mode.View}
          errorMessage={
            errors ? errors.industryDescription?.message : undefined
          }
          readOnly={readOnly}
        />
        <Checkbox
          control={control}
          name="isPhysicalLocation"
          label={t("locations_Physical")}
          disabled={mode === Mode.View || readOnly}
        />
        {isPhysicalLocation ? (
          <Select
            control={control}
            name="establishmentType"
            label={t("location_form_establishment_type_label")}
            options={getEstablishmentTypes()}
            errorMessage={errors.establishmentType?.message}
            readOnly={readOnly}
          />
        ) : null}
        {isPhysicalLocation && (
          <>
            <CountrySelect
              control={control}
              errors={errors}
              disabled={mode === Mode.View}
              readOnly={readOnly}
            />
            <Input
              control={control}
              label={t("locations_Address_1")}
              name="address.address1"
              rules={{
                required: t("required_message_address"),
                maxLength: {
                  value: 255,
                  message: t("location_Form_Address_Max_Length_Validation"),
                },
              }}
              disabled={mode === Mode.View}
              errorMessage={
                errors ? errors.address?.address1?.message : undefined
              }
              readOnly={readOnly}
            />
            <Input
              control={control}
              label={t("locations_Address_2")}
              name="address.address2"
              rules={{
                maxLength: {
                  value: 255,
                  message: t("location_Form_Address_Max_Length_Validation"),
                },
              }}
              disabled={mode === Mode.View}
              errorMessage={
                errors ? errors.address?.address2?.message : undefined
              }
              readOnly={readOnly}
            />
            <Input
              control={control}
              label={t("locations_City")}
              name="address.city"
              rules={{
                required: t("required_message_city"),
                maxLength: {
                  value: 100,
                  message: t("max_length_100_char_form_validation"),
                },
              }}
              disabled={mode === Mode.View}
              errorMessage={errors ? errors.address?.city?.message : undefined}
              readOnly={readOnly}
            />
            <StateProvinceSelect
              country={countrySelected}
              setValue={setValue as UseFormSetValue<FormLocation>}
              getValues={getValues as UseFormGetValues<FormLocation>}
              control={control}
              disabled={mode === Mode.View || !countrySelected}
              errors={errors}
              readOnly={readOnly}
            />
            <Input
              control={control}
              label={t("locations_Zip_Code")}
              name="address.postalCode"
              rules={{
                maxLength: {
                  value: 16,
                  message: t("location_Form_Postal_Code_Max_Length_Validation"),
                },
              }}
              disabled={mode === Mode.View}
              errorMessage={
                errors ? errors.address?.postalCode?.message : undefined
              }
              readOnly={readOnly}
            />
            <Input
              control={control}
              name="sicCode"
              label={`SIC ${t("universal_lower_code")}`}
              disabled={mode === Mode.View}
              errorMessage={errors ? errors.sicCode?.message : undefined}
              readOnly={readOnly}
            />
            <Input
              control={control}
              name="naicsCode"
              label={`NAICS ${t("universal_lower_code")}`}
              disabled={mode === Mode.View}
              errorMessage={errors ? errors.naicsCode?.message : undefined}
              readOnly={readOnly}
            />
            <Input
              control={control}
              name="ein"
              label="EIN"
              disabled={mode === Mode.View}
              errorMessage={errors ? errors.ein?.message : undefined}
              readOnly={readOnly}
            />
          </>
        )}
      </form>
      <UnsavedChangesPrompt isDirty={isDirty} history={history} />
    </>
  );
};

export { AddEditLocationFormNew };
